import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useCreateMtoRunMutation } from '@shared/api';
import type { MtoModuleId } from '@entities/mto';
import { apiErrorMessage, moduleDocTypes, mtoModule } from '@entities/mto';
import { ACCEPT_ANY } from '@shared/lib/fileAccept';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Select, Label, Field } from '@shared/ui/Input';
import { IconFile, IconTrash } from '@shared/ui/icons';
import { fmtSize, nextDocId } from '../helpers';
import {
  Err,
  FileHint,
  FileItem,
  FileList,
  FileName,
  FileRow,
  FileSize,
  FormTitle,
  ReadOnlyHint,
  RemoveBtn,
  TypeHint,
} from '../styles';
import type { AttachedDoc } from '../types';

export function NewRunForm({
  activeModule,
  canWrite,
  onRunCreated,
}: {
  activeModule: MtoModuleId;
  canWrite: boolean;
  onRunCreated: (runId: string) => void;
}) {
  const moduleMeta = mtoModule(activeModule);
  const [docs, setDocs] = useState<AttachedDoc[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createRun, { isLoading: creating }] = useCreateMtoRunMutation();

  useEffect(() => {
    setDocs([]);
    setError(null);
  }, [activeModule]);

  /** Набор типов документов активного модуля. */
  const docTypes = useMemo(() => moduleDocTypes(activeModule), [activeModule]);

  const hasUnsetType = useMemo(() => docs.some((d) => !d.docType), [docs]);

  /** Файлы из зоны загрузки: перетаскивание, выбор в диалоге, папка целиком
   *  или архив — `FileDropZone` сводит все способы к плоскому списку. */
  function handleAddFiles(files: File[]) {
    const first = docTypes[0];
    if (files.length === 0 || !first) return;
    setError(null);
    setDocs((prev) => [
      ...prev,
      ...files.map((file) => ({ id: nextDocId(), file, docType: first.docType })),
    ]);
  }

  function setDocType(id: string, docType: string) {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, docType } : d)));
  }

  function removeDoc(id: string) {
    setDocs((prev) => prev.filter((d) => d.id !== id));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (docs.length === 0) {
      setError('Приложите хотя бы один документ и выберите его тип.');
      return;
    }
    if (hasUnsetType) {
      setError('Для каждого документа выберите тип.');
      return;
    }
    // Контракт: текстовое поле `module` + каждый файл под именем поля = его
    // docType (doc_type).
    const fd = new FormData();
    fd.append('module', activeModule);
    for (const d of docs) fd.append(d.docType, d.file);
    try {
      const res = await createRun(fd).unwrap();
      setDocs([]);
      onRunCreated(res.run_id);
    } catch (err) {
      // Сообщение backend как есть (внятное, по-русски: «недопустимый тип
      // документа…»). Общий текст вводил бы в заблуждение.
      setError(
        apiErrorMessage(err) ??
          'Не удалось запустить обработку. Попробуйте снова; если ошибка повторяется — обратитесь к администратору.',
      );
      // eslint-disable-next-line no-console
      console.error('createMtoRun failed', err);
    }
  }

  return (
    <Card>
      <FormTitle>Новая обработка · {moduleMeta?.title.toLowerCase()}</FormTitle>
      <form onSubmit={handleSubmit}>
        <Field>
          <Label>Документы обработки</Label>
          {canWrite ? (
            <>
              {/* ACCEPT_ANY: принимаем любой формат (Excel, PDF, скан, фото,
                  DOCX…), извлечение на backend разберётся само. */}
              <FileDropZone
                multiple
                compact
                accept={ACCEPT_ANY}
                hint="Или нажмите, чтобы выбрать. Excel, PDF, скан, фото, Word — тип укажете для каждого файла"
                onFiles={handleAddFiles}
              />
              <FileHint>{moduleMeta?.filesHint}</FileHint>
            </>
          ) : (
            <ReadOnlyHint>Только просмотр — загрузка документов недоступна.</ReadOnlyHint>
          )}

          {docs.length > 0 && (
            <FileList>
              {docs.map((d) => {
                const dt = docTypes.find((t) => t.docType === d.docType);
                const selectId = `mto-doctype-${d.id}`;
                return (
                  <FileItem key={d.id}>
                    <FileRow>
                      <FileName title={d.file.name}>
                        <IconFile size={15} />
                        {d.file.name}
                      </FileName>
                      <FileSize>{fmtSize(d.file.size)}</FileSize>
                      <RemoveBtn
                        type="button"
                        onClick={() => removeDoc(d.id)}
                        aria-label={`Убрать файл ${d.file.name}`}
                        title="Убрать файл"
                      >
                        <IconTrash size={16} />
                      </RemoveBtn>
                    </FileRow>
                    {docTypes.length > 1 && (
                      <>
                        <Label htmlFor={selectId}>Тип документа</Label>
                        <Select
                          id={selectId}
                          value={d.docType}
                          onChange={(e) => setDocType(d.id, e.target.value)}
                          aria-invalid={!d.docType}
                        >
                          {docTypes.map((t) => (
                            <option key={t.docType} value={t.docType}>
                              {t.label}
                            </option>
                          ))}
                        </Select>
                      </>
                    )}
                    {dt && <TypeHint>{dt.hint}</TypeHint>}
                  </FileItem>
                );
              })}
            </FileList>
          )}
        </Field>

        {error && <Err>{error}</Err>}
        <div>
          <Button
            type="submit"
            $variant="primary"
            disabled={creating || !canWrite || hasUnsetType}
            title={!canWrite ? 'Только просмотр' : undefined}
          >
            {creating ? 'Запуск…' : 'Запустить обработку'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
